import * as Sentry from "@sentry/react";
import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { ArrowLeft, Plus } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  addressesQueryOptions,
  updateUserInfoFn,
  userInfoQueryOptions,
} from "@/api/auth/auth.query.options";
import { OrderHistorySection } from "@/components/business/order-history/OrderHistorySection";
import { AddressList } from "@/components/business/profile/AddressList";
import { CreateAddressSheet } from "@/components/business/profile/CreateAddressSheet";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CompactLoader } from "@/components/ui/loader";
import { noindexHead } from "@/lib/seo";
import { currentUserAtom, isAuthAtom } from "@/stores/auth.store";

export const Route = createFileRoute("/business/$slug_/profile")({
  ssr: false,
  head: noindexHead,
  validateSearch: (s: Record<string, unknown>) => ({
    scrollTo: typeof s.scrollTo === "string" ? s.scrollTo : undefined,
  }),
  pendingComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <CompactLoader />
    </div>
  ),
  component: Sentry.withProfiler(ProfilePage, { name: "ProfilePage" }),
});

function ProfileField({
  field,
  label,
  className,
}: {
  field: {
    name: string;
    state: {
      value: string;
      meta: {
        isTouched: boolean;
        isValid: boolean;
        errors: ({ message?: string } | undefined)[];
      };
    };
    handleChange: (v: string) => void;
    handleBlur: () => void;
  };
  label: string;
  className?: string;
}) {
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  return (
    <Field className={className} data-invalid={isInvalid}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Input
        id={field.name}
        name={field.name}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={isInvalid}
      />
      {isInvalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { slug } = useParams({ strict: false }) as { slug: string };
  const isAuth = useAtomValue(isAuthAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const queryClient = useQueryClient();

  const { data: user, isLoading: userLoading } = useQuery(
    userInfoQueryOptions(isAuth),
  );
  const { data: addresses, isLoading: addressesLoading } = useQuery(
    addressesQueryOptions(isAuth),
  );
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const orderHistoryRef = useRef<HTMLDivElement>(null);
  const { scrollTo } = Route.useSearch();

  useEffect(() => {
    if (scrollTo === "orders") {
      orderHistoryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [scrollTo]);

  const form = useForm({
    defaultValues: { firstName: "", lastName: "" },
    validators: {
      onSubmit: ({ value }) => {
        if (!value.firstName.trim() || !value.lastName.trim()) {
          return t("errors.nameRequired");
        }
        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      await toast.promise(
        updateUserInfoFn(value.firstName, value.lastName).then((updated) => {
          setCurrentUser(updated);
          queryClient.invalidateQueries({ queryKey: ["userinfo"] });
        }),
        {
          loading: t("common.status.loading"),
          success: t("common.status.updated"),
          error: t("errors.generic"),
        },
      );
    },
  });

  useEffect(() => {
    if (!user) return;
    form.setFieldValue("firstName", user.firstName);
    form.setFieldValue("lastName", user.lastName);
  }, [user, form]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="relative min-h-screen"
    >
      <div className="sticky top-0 z-20 flex items-center gap-3 p-4 bg-gradient-to-b from-background via-background/40 to-transparent backdrop-blur-sm">
        <button
          type="button"
          data-testid="profile.back"
          onClick={() => router.history.back()}
          className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-bold text-foreground">
          {t("business.profile.title")}
        </h1>
      </div>

      <div className="px-4 pb-10 space-y-4 max-w-lg mx-auto">
        <Card className="rounded-2xl shadow-none">
          <CardHeader>
            <CardTitle>{t("business.profile.myProfile")}</CardTitle>
            <CardDescription>
              {t("business.profile.profileDescription")}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {userLoading ? (
              <div data-testid="profile.account.loading" className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="h-4 w-16 rounded bg-muted animate-pulse" />
                    <div className="h-10 rounded-xl bg-muted animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <form
                data-testid="profile.account.form"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <FieldGroup className="gap-3">
                  <div className="flex gap-3">
                    <form.Field name="firstName">
                      {(field) => (
                        <ProfileField
                          field={field}
                          label={t("business.profile.firstName")}
                          className="flex-1"
                        />
                      )}
                    </form.Field>
                    <form.Field name="lastName">
                      {(field) => (
                        <ProfileField
                          field={field}
                          label={t("business.profile.lastName")}
                          className="flex-1"
                        />
                      )}
                    </form.Field>
                  </div>

                  <Field>
                    <FieldLabel>{t("business.profile.phone")}</FieldLabel>
                    <Input
                      value={
                        user
                          ? `${user.phoneCountryCode} ${user.phoneNumber}`
                          : ""
                      }
                      disabled
                    />
                  </Field>

                  {user?.emailAddress && (
                    <Field>
                      <FieldLabel>{t("business.profile.email")}</FieldLabel>
                      <Input value={user.emailAddress} disabled />
                    </Field>
                  )}
                </FieldGroup>

                <form.Subscribe selector={(s) => s.errors ?? []}>
                  {(errors) =>
                    errors.length > 0 && (
                      <p className="text-xs text-destructive mt-3">
                        {String(errors[0])}
                      </p>
                    )
                  }
                </form.Subscribe>

                <CardFooter className="px-0 pt-4 pb-0 justify-end">
                  <form.Subscribe
                    selector={(s) => ({
                      isSubmitting: s.isSubmitting,
                      values: s.values,
                    })}
                  >
                    {({ isSubmitting, values }) => (
                      <Button
                        type="submit"
                        data-testid="profile.account.save"
                        disabled={
                          isSubmitting ||
                          !values.firstName.trim() ||
                          !values.lastName.trim()
                        }
                        className="rounded-xl"
                      >
                        {t("business.profile.saveChanges")}
                      </Button>
                    )}
                  </form.Subscribe>
                </CardFooter>
              </form>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-none">
          <CardHeader className="pb-3 flex-row items-start justify-between">
            <div>
              <CardTitle>{t("business.profile.savedAddresses")}</CardTitle>
              <CardDescription className="mt-1">
                {t("business.profile.addressesDescription")}
              </CardDescription>
            </div>
            <Button
              type="button"
              data-testid="profile.addresses.add"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full shrink-0"
              onClick={() => setAddAddressOpen(true)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {addressesLoading ? (
              <div
                data-testid="profile.addresses.loading"
                className="flex items-center justify-center py-8"
              >
                <CompactLoader />
              </div>
            ) : addresses && addresses.length > 0 ? (
              <AddressList addresses={addresses} />
            ) : (
              <div
                data-testid="profile.addresses.empty"
                className="flex flex-col items-center gap-3 text-center py-4"
              >
                <img
                  src="/empty.svg"
                  alt=""
                  className="w-28 h-28 opacity-50 dark:invert"
                />
                <p className="text-sm text-muted-foreground">
                  {t("business.profile.noAddresses")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div ref={orderHistoryRef}>
          <OrderHistorySection slug={slug} />
        </div>
      </div>

      <CreateAddressSheet
        open={addAddressOpen}
        onClose={() => setAddAddressOpen(false)}
      />
    </motion.div>
  );
}
